/**
 * @fileoverview Passwordless auth telemetry (WEB-1212).
 *
 * Lives next to `onboarding.ts` on purpose: signup completion
 * (`signup_complete` / the onboarding takeover) chains off a successful
 * auth, and these three events are the method-level funnel in front of it.
 *
 * | PostHog event name   | Trigger                                      | Payload                                      |
 * | -------------------- | -------------------------------------------- | -------------------------------------------- |
 * | `auth_method_viewed` | `/login` or `/signup` mount                  | `{ surface: 'login' \| 'signup' }`           |
 * | `auth_started`       | The visitor commits to a method              | `{ method }`                                 |
 * | `auth_completed`     | The session is actually established          | `{ method, is_new_user }`                    |
 *
 * **No PII.** Payloads never include an email address, name, or token.
 * `accountEmail` is accepted only so the call site can drop events for
 * known E2E principals before `track()` runs. The address is not forwarded.
 *
 * **E2E exclusion.** `User.isE2EAccount` is not on the client user payload,
 * so exclusion is by the known seeded addresses (`test@loci.dev`,
 * `test-admin@loci.dev`, `test.agent@loci.city`, `test.agent.admin@loci.city`)
 * plus the `@loci.test` fixture suffix. A viewed event fired before an
 * address is known still emits — there is no account to exclude yet.
 *
 * **`is_new_user` gap (API).** Magic-link consume and the OAuth callback
 * redirect do not currently tell the client whether the account was just
 * created (`newUserCreated` is audit-only on the server). `resolveIsNewUser`
 * honors `newUserCreated` / `isNewUser` when a response grows them, and
 * otherwise treats a `createdAt` within two minutes as new. Password and
 * passkey sign-in pass `false` (those methods do not create accounts).
 * PostHog verification of `auth_completed` by method is a staging check
 * after deploy, once API returns `newUserCreated` on consume.
 *
 * Capture flows through `lib/analytics.ts#track` and no-ops without consent.
 */
import { track } from '@/lib/analytics';

export const AUTH_EVENT_NAMES = {
  AUTH_METHOD_VIEWED: 'auth_method_viewed',
  AUTH_STARTED: 'auth_started',
  AUTH_COMPLETED: 'auth_completed',
} as const;

export type AuthEventName = (typeof AUTH_EVENT_NAMES)[keyof typeof AUTH_EVENT_NAMES];

/** Methods the login and signup surfaces can start. Facebook is the wire id. */
export const AUTH_METHODS = [
  'magic_link',
  'google',
  'apple',
  'facebook',
  'tiktok',
  'passkey',
  'password',
] as const;

export type AuthMethod = (typeof AUTH_METHODS)[number];

export type AuthSurface = 'login' | 'signup';

const AUTH_METHOD_SET: ReadonlySet<string> = new Set(AUTH_METHODS);

/**
 * Seeded and agent principals. Compared case-insensitively. Never sent to
 * PostHog — membership only decides whether to emit.
 */
const E2E_ACCOUNT_EMAILS: ReadonlySet<string> = new Set([
  'test@loci.dev',
  'test-admin@loci.dev',
  'test.agent@loci.city',
  'test.agent.admin@loci.city',
]);

const PENDING_AUTH_METHOD_KEY = 'loci.auth.pendingMethod';

/** Accounts created in this window count as new when no explicit flag is present. */
const NEW_ACCOUNT_WINDOW_MS = 2 * 60 * 1000;

export function isAuthMethod(value: unknown): value is AuthMethod {
  return typeof value === 'string' && AUTH_METHOD_SET.has(value);
}

/**
 * True for known E2E / fixture accounts. An empty address is not an E2E
 * account — the caller has not identified anyone yet.
 */
export function isE2EAnalyticsAccount(email: string | null | undefined): boolean {
  if (!email) return false;
  const normalized = email.trim().toLowerCase();
  if (E2E_ACCOUNT_EMAILS.has(normalized)) return true;
  return normalized.endsWith('@loci.test');
}

/** False when the known account is an E2E principal. Unknown addresses still emit. */
export function shouldEmitAuthAnalytics(email?: string | null): boolean {
  return !isE2EAnalyticsAccount(email);
}

/**
 * Read an explicit new-user flag, then a recent `createdAt`, off a consume
 * body or a `/auth/me` user. Returns false when neither signal is present.
 */
export function resolveIsNewUser(source: unknown): boolean {
  if (!source || typeof source !== 'object') return false;
  const record = source as Record<string, unknown>;
  if (typeof record.newUserCreated === 'boolean') return record.newUserCreated;
  if (typeof record.isNewUser === 'boolean') return record.isNewUser;
  if (createdAtIsNew(record.createdAt)) return true;
  if (record.user && record.user !== source) return resolveIsNewUser(record.user);
  return false;
}

function createdAtIsNew(value: unknown): boolean {
  if (typeof value !== 'string') return false;
  const created = Date.parse(value);
  if (Number.isNaN(created)) return false;
  const age = Date.now() - created;
  return age >= 0 && age < NEW_ACCOUNT_WINDOW_MS;
}

export function trackAuthMethodViewed(surface: AuthSurface): void {
  track(AUTH_EVENT_NAMES.AUTH_METHOD_VIEWED, { surface });
}

export function trackAuthStarted(method: AuthMethod, accountEmail?: string | null): void {
  if (!shouldEmitAuthAnalytics(accountEmail)) return;
  track(AUTH_EVENT_NAMES.AUTH_STARTED, { method });
}

export function trackAuthCompleted(input: {
  method: AuthMethod;
  isNewUser: boolean;
  accountEmail?: string | null;
}): void {
  if (!shouldEmitAuthAnalytics(input.accountEmail)) return;
  track(AUTH_EVENT_NAMES.AUTH_COMPLETED, {
    method: input.method,
    is_new_user: input.isNewUser,
  });
}

/**
 * Remember which provider the visitor clicked before the full-page OAuth
 * redirect. `AppInitializer` consumes it on the `oauth_session` return to
 * emit `auth_completed`. Session storage only — never the URL, never PostHog.
 */
export function rememberPendingAuthMethod(method: AuthMethod): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(PENDING_AUTH_METHOD_KEY, method);
  } catch {
    // Private mode and disabled storage must not block the redirect.
  }
}

export function consumePendingAuthMethod(): AuthMethod | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(PENDING_AUTH_METHOD_KEY);
    window.sessionStorage.removeItem(PENDING_AUTH_METHOD_KEY);
    return isAuthMethod(raw) ? raw : null;
  } catch {
    return null;
  }
}
